import { DatePipe } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AppSettings } from '../api/models/appSettings';
import { ProgressBarDetails } from '../api/models/ProgressBarDetails';
import { SessionResponse } from '../api/models/SessionResponse';
import { ApiErrorCode, ClientKeyValues, CodeErrorCode, CommonConstants, CONTINENTAL_REGIONS, COUNTRY_LANGUAGE, ServiceURLConstants, SUPPORTED_LANGUAGES, SUPPORT_PHONE_NUMBERS, UPPLM_CURRENT_VERSION, UPS_PICKUP_POINT } from '../constants/upplm.constants';
import { ProfileInfoModalRequest, UPPLMResponseModal } from '../interface/upplm.common.interface';
import { LocationDetails } from '../interface/upplm.placardInformation';
import { UpplmGenericDictionary } from '../services/upplm.genericDictionary';
import { UpplmApiService } from './upplm.api.services';
import { ConfigurationService } from './upplm.configuration.service';
import { UpplmErrorHandlerService } from './upplm.errorHandler.service';
import { ProgressBarService } from './upplm.progressbar.service';
import { UpplmSessionService } from './upplm.session.service';

@Injectable()
export class UpsCommonService {
    public TransactionID: string | null;
    public contentTexts: UpplmGenericDictionary<String>;
    private _xLocale: string;
    public locale: string;
    public locale_country: string;
    public locale_lang: string;
    public companyName: string;
    public companyNickname: string;
    public companyID: string;
    public loggedInUserId: string;
    public loggedInUUID: string;
    public userAdminUserName: string;
    public userAdminUserID: string;
    public isQVA_Admin: boolean = false;
    public isQVA_User: boolean = false;
    public hasCompany: boolean = false;
    public _isFirstQVAccordian: boolean = true;
    public _isSecondQVAccordian: boolean = false;
    public _isThirdQVAccordian: boolean;
    public _isForthQVAccordian: boolean;
    public _isfirstQVAccordianValue: any;
    public _issecondQVAccordianValue: any;
    public _isthirdQVAccordianValue: any;
    public _isforthQVAccordianValue: any;
    public _isFirstUserAccordian: boolean = true;
    public _isSecondUserAccordian: boolean;
    public _isThirdUserAccordian: boolean;
    public _isFourthUserAccordian: boolean;
    public _isfirstUserAccordianValue: any;
    public _issecondUserAccordianValue: any;
    public _isthirdUserAccordianValue: any;
    public _isfourthUserAccordianValue: any;
    public _isSelfEnroll: boolean = true;
    public addAccountFlowType: string;
    public isSelfIRAAddAccountFlow: boolean = false;
    public selfIRAAddAccountFlowObj: any;
    public selfEnrollAccountFlowObj: any;
    private contentRequestModal: {
        Locale: string | null,
        PageIds: any | null,
        TransactionId: string | null,
        IsContainer: boolean
    };
    private initRequestModal: {
        isCreateOutboundFlow: boolean,
        isCreateInboundFlow: boolean,
        clientId: string
    }
    public isProfileApiError: boolean = false;
    public profileErrorObj: any;
    public cachedContainerPage: any;
    private _profileRequest: ProfileInfoModalRequest;
    public _profileResponse: UPPLMResponseModal;
    public _companyInfoResponse: UPPLMResponseModal;
    public apiErrorCode: string;
    public exceptionErrorCode: string;
    public isProd: boolean = false;
    public IOBB: string;
    public IsSelfEnrollOutBound: boolean = false;
    public IsSelfEnrollInBound: boolean = false;
    public URL_Parameters: string[];
    public appLoading: boolean = false;
    public appLoaded: boolean = false;
    public viewchangeValue: string;
    public chooseAddressObj: any;
    public companyType: string;
    public url: string;
    public returnURLFromViewService: string;
    public cacContentArray: any[] = [];
    public chooseAddressRoute: string;
    public addAccountPrivilegeType: string;
    public welcomeText: string;
    public titleText: string;
    public saveModalTitle: string;
    public updateNickNameTitle: string;
    public areyouSureTitle: string;
    public breadcrumbArray: any[];

    constructor(@Inject(UpplmApiService) private _apiService: UpplmApiService,
        @Inject(UpplmErrorHandlerService) private _errorHandlerService: UpplmErrorHandlerService,
        @Inject(UpplmSessionService) private _sessionService: UpplmSessionService,
        @Inject(ProgressBarService) private _pbService: ProgressBarService,
        @Inject(ConfigurationService) private _configurationService: ConfigurationService,
        private _datePipe: DatePipe) {
        this.contentTexts = new UpplmGenericDictionary<String>();
        this._profileRequest = new ProfileInfoModalRequest();
        this._profileResponse = new UPPLMResponseModal();
        this._companyInfoResponse = new UPPLMResponseModal();
    }
    viewChanged = new Subject();

    //Format a time in a friendly output. V. Gr. 15:00 to 03:00 pm
    formatAMPM(rawTime: string) {
            const [hour, minutes] = rawTime.split(':').map((value) => parseInt(value, 10));
            const today = new Date();
            const [month, day, year] = [
                today.getMonth(),
                today.getDate(),
                today.getFullYear(),
            ];
            const dateFromStr = new Date(year, month, day, hour, minutes);
            return this._datePipe.transform(dateFromStr, 'hh:mm aaa');
    }

    getRegionCountry(locale_country: string): string {
        return COUNTRY_LANGUAGE.find(x => x.key === locale_country).region.toString();
    }

    isEmeaCountry(countryCode: string): boolean {
        return COUNTRY_LANGUAGE.find(x => x.key === countryCode).region === CONTINENTAL_REGIONS.EMEA ? true : false;
    }

    getMessageCallUpsIfDifficulty(countryCode: string, defaultMessage: string): string {
        let message: string = '';
        const phoneNumber = SUPPORT_PHONE_NUMBERS.find(x => x.countryCode === countryCode).phoneNumber;
        message = defaultMessage.replace('%[y]%', phoneNumber);
        return message.replace('%[x]%', UPS_PICKUP_POINT);
    }

    getCountryByPlacardAddressInformation(locationDetails: LocationDetails): string {
        if (locationDetails && locationDetails.address) {
            return locationDetails.address.countryCode;
        }
        return this.locale_country;
    }

    getLocUrlParameter(sParam: string) {
        let sPageURL = decodeURIComponent(window.location.search.substring(1)),
            sURLVariables = sPageURL.split('&'),
            sParameterName,
            i;
        this.URL_Parameters = sURLVariables;
        for (i = 0; i < sURLVariables.length; i++) {
            sParameterName = sURLVariables[i].split('=');
            let myString = sURLVariables[i].substring(sURLVariables[i].indexOf('=') + 1);
            if (sParameterName[0] === sParam) {
                return sParameterName[1] === undefined ? true : myString;
            }
        }
    };

    getUrlParameters() {
        let sPageURL1 = decodeURIComponent(window.location.search.substring(1)),
            sURLVariables1 = sPageURL1.split('&');

        this.URL_Parameters = sURLVariables1;

    };

    getUrlParameter(parameter: string): string {
        let val: string = '';
        if (this.URL_Parameters) {
            this.URL_Parameters.forEach(param => {
                let temParameter = param.split('=');
                if (temParameter[0] === parameter) {
                    val = temParameter[1];
                }
            });
        }
        return val;
    };

    getUrlParams(url: string, params: string): string {
        if (params) {
            return `${url}?${params}`;
        }
        return url;
    }

    createURL(url: string, code: string) {
        if (url == ServiceURLConstants.GET_INIT) {
            let angularUrl = window.location.href;
            let params = null;
            if (angularUrl.includes("?")) {
                let array = angularUrl.split("?");
                params = array[1];
            }
            return this.getUrlParams(url, params);
        }
        else {
            if (code === null || code === undefined) {
                code = "NA";
            }
            let angularUrl = window.location.href;
            let params = null;
            if (angularUrl.includes("?")) {
                let array = angularUrl.split("?");
                let params1 = array[1].split("#");
                params = params1[0];
            }
            return this.getUseCaseNumberParams(params, url, this.TransactionID, code);
        }
    }

    getUseCaseNumberParams(params: string, url: string, transactionId: string, code: string): string {
        let addedParams: string = '';
        if (params) {
            transactionId = 'test';
            addedParams = `${params}&UPPLM_TRANSACTION_ID=${transactionId}${code}`;
            let myVal: any = this.getLocUrlParameter("UPPLM_USE_CASE_NUMBER");
            if (typeof myVal === 'undefined') {
                url = url.concat('?',addedParams, '&UPPLM_USE_CASE_NUMBER=-1');
            }
        }
        return url;
    }

    initializeUPPLMApp(): Promise<any> {
        this.appLoading = true;
        let url = window.location.href;
        let allContainers: any[] = [];
        let currentURL = window.location.href;
        var key = {};
        let name = "loc";
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        let regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        let results = regex.exec(currentURL);
        let loc = CommonConstants.UPPLM_DEFAULT_LOCALE;
        loc = this.getLocValue(results, currentURL);
        this.locale = loc;

        this.setDefaultLocale();

        this.locale_country = this.locale.substring(3, 5);
        this.locale_lang = this.locale.substring(0, 2);

        if (url.indexOf('?') > 0) {
            var getString = url.split('?')[1];
            var get = getString.split('&');

            for (var i = 0; i < get.length; i++) {
                var temp = get[i].split('=');
                key[temp[0]] = unescape(decodeURI(temp[1]));
            }
        }

        let Clientkeyalias = "";
        let ClientkeyId = "";
        let clientKeyValues: ClientKeyValues;
        clientKeyValues = this.getClientKeyValues(key);
        Clientkeyalias = clientKeyValues.clientKeyAlias;
        ClientkeyId = clientKeyValues.clientKeyId;

        this._pbService.pb = new ProgressBarDetails();
        this._pbService.pb.showStatusBar = true;

        //Getting Configuration
        this._configurationService.getConfiguration(ServiceURLConstants.GET_CONFIGURATION).subscribe((response: AppSettings) => {
            this._configurationService.appSettings = response;
        });


        //Creating Session
        this._sessionService.createSessionWithParameters(this.createURL(ServiceURLConstants.CREATE_SESSION, ApiErrorCode.CREATE_SESSION), Clientkeyalias, ClientkeyId).subscribe((response: SessionResponse) => {
            this._sessionService.mapSessionResponse(response);
        });

        this.url = currentURL;
        this.initRequestModal = { isCreateInboundFlow: false, isCreateOutboundFlow: false, clientId: '' };

        this.TransactionID = "test";
        // Get Transaction Id
        let message = new Promise<void>((resolve, reject) => {
            let init_url = this.createURL(ServiceURLConstants.GET_INIT, "IN1");
            this._apiService.PostServiceAPI(init_url, this.initRequestModal).subscribe((response: UPPLMResponseModal) => {
                this._profileResponse.TransactionId = response.TransactionId;
                this._profileResponse = response;
                this.isProd = this._profileResponse.IS_PROD;
                this.TransactionID = this._profileResponse.TransactionId;
                this.appLoading = false;
                this.appLoaded = true;
                this.IsSelfEnrollOutBound = this._profileResponse.IsCreateOutboundFlow;
                if (this._profileResponse.IsValid) {
                    this.setCompanyData();
                }
                else {
                    this.TransactionID = this._profileResponse.TransactionId;
                    if (!this.IsSelfEnrollOutBound) {
                        this.configureErrorOnErrorType(this._profileResponse.ErrorType);
                    }
                    else {
                        this.profileErrorObj = this._profileResponse;
                    }
                }
                let url = this.createURL(ServiceURLConstants.GET_HTML_CONTENT, "WEMS");
                this.contentRequestModal = { IsContainer: false, PageIds: allContainers, Locale: this.locale, TransactionId: this.TransactionID };
                this._apiService.executeWEMS(url, this.contentRequestModal).subscribe(response => {
                        resolve();
                },
                    error => {
                        resolve();
                    });
            }, error => {
                resolve();
            });
        });
        return message;
    }

    setCompanyData() {
        if (this._profileResponse.ProfileResponseObj.CompanyId) {
            this.setQVAProfile();
            this.loggedInUserId = this._profileResponse.ProfileResponseObj.LoggedInUserId;
            this.loggedInUUID = this._profileResponse.ProfileResponseObj.LoggedInUUID;
            this.hasCompany = true;
            this.companyID = this._profileResponse.ProfileResponseObj.CompanyId;
            this.companyName = this._profileResponse.ProfileResponseObj.CompanyName;
            this.setCompanyInfo();
        }
        else {
            // No Company associated with User, redirect to QV info page.
            this.hasCompany = false;
        }
    }

    setDefaultLocale() {
        if (!SUPPORTED_LANGUAGES.find(x => x.value === this.locale)) {
            this.locale = "en_US";
        }
    }

    getLocValue(results: RegExpExecArray, currentURL: string): any {
        if (results) {
            return decodeURIComponent(results[1].replace(/\+/g, ' '));
        } else {
            let a = currentURL.split("/");
            if (a.length >= 5) {
                return a[4];
            }
        }
    }

    configureErrorOnErrorType(errorType: string) {
        switch (errorType) {
            case CommonConstants.CV_HARD:
                //show hard error message    
                this.validateNoCompanyAssociatedToUser();
                this.isProfileApiError = true;
                this.apiErrorCode = ApiErrorCode.GET_PROFILE_INFO;
                this.profileErrorObj = this._profileResponse;
                break;
            case CommonConstants.CV_TRANSIENT:
                //Redirect to Technical Error page
                this.isProfileApiError = true;
                this.profileErrorObj = this._profileResponse;
                this.apiErrorCode = ApiErrorCode.GET_PROFILE_INFO;
                break;
            case CommonConstants.CV_EXCEPTION:
                //Redirect to Technical Error page                            
                this.isProfileApiError = true;
                this.exceptionErrorCode = CodeErrorCode.GET_PROFILE_INFO;
                this.profileErrorObj = this._profileResponse;
        }
    }

    validateNoCompanyAssociatedToUser() {
        if (this._profileResponse.ErrorCode == "3046" || this._profileResponse.ErrorCode == "3048") {
            // No Company associated with User, redirect to QV info page.
            this.hasCompany = false;
        }
    }

    setCompanyInfo() {
        if (this._profileResponse.ResponseObject) {
            this._companyInfoResponse = this._profileResponse.ResponseObject;
            if (this._companyInfoResponse.ResponseObject) {
                this.companyNickname = this._companyInfoResponse.ResponseObject.companyAlias;
                this.companyType = this._companyInfoResponse.ResponseObject.companyType;
                if (!this._companyInfoResponse.ResponseObject.selfEnrolled) {
                    this._isSelfEnroll = false;
                }
            }
        }
    }

    setQVAProfile() {
        if (this._profileResponse.ProfileResponseObj.IsQVAdminUser) {
            this.isQVA_Admin = true;
        }
        if (this._profileResponse.ProfileResponseObj.IsQVUser) {
            this.isQVA_User = true;
        }
    }

    getClientKeyValues(key: {}): ClientKeyValues {
        let clientKeyValues: ClientKeyValues = {
            clientKeyAlias: '',
            clientKeyId: ''
        }

        for (var item in key) {
            if (item !== "loc") {
                if (item.toLowerCase() == "nickname") {
                    clientKeyValues.clientKeyAlias = key[item];
                } else if (item.toLowerCase() == "clientkeynumber") {
                    clientKeyValues.clientKeyId = key[item];
                }
            }
        }
        return clientKeyValues;
    }

    private handleError(data?: any) {
        return (error: any) => {
            // do nothing.
        }
    }

    changeScreen(url: string) {
        this.viewChanged.next(url);
    }

    addContentTexts(containerCommon: any) {
        for (let j = 0; j < containerCommon.item.length; j++) {
            this.contentTexts.Add(containerCommon.item[j].key, containerCommon.item[j].value);
        }
    }

    processCACContentData(cacContent: any) {
        this.cacContentArray = [];
        let cacItem = cacContent.item;
        for (let i = 0; i < cacItem.length; i++) {
            let keyValueObject = {
                key: cacItem[i].key,
                value: cacItem[i].value
            };
            this.cacContentArray.push(keyValueObject);
        }
    }

    processModalTitles() {
        if (this.contentTexts.Count() > 0) {
            this.saveModalTitle = this.contentTexts.Item('cv.commonMsg.saveModal').toString();
            this.updateNickNameTitle = this.contentTexts.Item('cv.commonMsg.UpdateNickName').toString();
            this.areyouSureTitle = this.contentTexts.Item('cv.commonMsg.areyouSure?').toString();


            if (!this.saveModalTitle) {
                this.saveModalTitle = "We've saved your changes";
            }
            if (!this.updateNickNameTitle) {
                this.updateNickNameTitle = "Update Nickname";
            }
            if (!this.areyouSureTitle) {
                this.areyouSureTitle = "Are you Sure ?";
            }
        } else {
            this.updateNickNameTitle = "Update Nickname";
            this.saveModalTitle = "We've saved your changes";
            this.areyouSureTitle = "Are you Sure ?";
        }
    }

    public versionApp(): void {
        console.log('app.version: ' + UPPLM_CURRENT_VERSION);
    }
}